import { useEffect, useRef, useState } from 'react';

/**
 * A espera — o jeito ÚNICO de o painel e o app dizerem "estou buscando".
 *
 * Duas peças, e a regra de qual usar:
 *
 *   <Veu>        cobre um pedaço da tela que JÁ TEM conteúdo. É o padrão.
 *   <Carregando> a esfera sozinha, para quando não há nada para cobrir
 *                (primeira abertura, um vão vazio).
 *
 * Escolhido em 28/08/2026 comparando três protótipos com o Felipe. Perdeu a
 * alternativa de trocar o conteúdo pela esfera: ela derruba o layout (medi no
 * Balcão — 1639px viram 606px), a rolagem se perde e quem estava na linha 12 do
 * extrato volta para a 1. O véu não move um pixel.
 *
 * O visual mora em `compartilhado/espera.css`, importado pelos dois builds.
 */

export function Veu({
  esperando,
  texto = 'carregando…',
  rasa = false,
  presa = false,
  className,
  children,
}: {
  /** Enquanto true, o pano cobre o conteúdo. */
  esperando: boolean;
  texto?: string | null;
  /** Bloco baixo (uma linha, um cartão curto): só a órbita, sem o texto. */
  rasa?: boolean;
  /**
   * O `.espera-area` É o contêiner que rola (`overflow-y: auto`), e não só um
   * pedaço dentro dele — Conversas, Cashback e Moedas. Sem isto, `inset: 0`
   * cobre o topo da LISTA, e quem está lida 30 linhas abaixo não vê véu
   * nenhum: ele ficou lá em cima, fora da tela. `presa` gruda o véu na janela
   * visível de rolagem, onde o olho está, em vez de no topo do conteúdo.
   */
  presa?: boolean;
  /** Vai junto no invólucro — para ele herdar o `flex: 1` de quem o cerca. */
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className ? `espera-area ${className}` : 'espera-area'} aria-busy={esperando}>
      {children}
      {esperando && (
        <div className={`espera-veu${rasa ? ' rasa' : ''}${presa ? ' presa' : ''}`}>
          {/*
           * ⚠️ O véu come o clique — está no CSS, e é metade do trabalho dele:
           * o botão que ficou atrás não é clicável de novo por engano.
           *
           * Não é a defesa principal, porém. Teclado ainda alcança o que está
           * atrás, e nada disso vale contra dois cliques no MESMO instante. A
           * trava de verdade é o `useAgir`, que barra na origem.
           */}
          <Carregando texto={rasa ? null : texto} tamanho={rasa ? 26 : 44} />
        </div>
      )}
    </div>
  );
}

/**
 * Carregando — a esfera da marca percorrendo a própria órbita.
 *
 * A ideia não é o giro: é o REPOUSO. A esfera desacelera até a posição exata em
 * que vive no símbolo, então o carregamento não tem uma marca colada em cima —
 * ele VIRA a marca.
 *
 * Três decisões que valem mais que a animação:
 *
 * 1. APARECE NA HORA. O padrão é zero de atraso.
 *
 *    O argumento contra era o piscar: um spinner que aparece por 120ms e some
 *    registra movimento em vez de informação. O argumento a favor venceu, e é
 *    melhor — tela em branco por 200ms parece travamento, e aqui o que aparece
 *    não é um spinner genérico, é a marca. `atraso` continua existindo para
 *    quem quiser segurar.
 *
 * 2. TAMANHO MÍNIMO. Abaixo de 20px a órbita deixa de ser legível e vira um
 *    pontinho tremendo. Nesse caso fica só a esfera pulsando.
 *
 * 3. O POUSO SÓ ONDE VALE. Ele custa ~700ms de espera DEPOIS de o dado já ter
 *    chegado. Na abertura do painel isso é o app nascendo e a lojista lê como
 *    tal; numa lista que recarrega, é meio segundo de nada. Por isso o pouso é
 *    opt-in (`pronto`), e não o comportamento padrão.
 */
export function Carregando({
  texto = 'carregando…',
  tamanho = 44,
  atraso = 0,
  pronto = false,
  aoPousar,
}: {
  texto?: string | null;
  tamanho?: number;
  /** ms antes de aparecer. Zero para telas que já se sabe que demoram. */
  atraso?: number;
  /** Vira true quando o dado chegou: dispara o pouso em vez do sumiço. */
  pronto?: boolean;
  /** Chamado quando a esfera terminou de pousar. */
  aoPousar?: () => void;
}) {
  const [visivel, setVisivel] = useState(atraso === 0);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (atraso === 0) return;
    const t = setTimeout(() => setVisivel(true), atraso);
    return () => clearTimeout(t);
  }, [atraso]);

  /*
   * O pouso.
   *
   * Congela a esfera onde ela está (senão ela salta para o começo), desliga a
   * animação e leva o `offset-distance` até 100% — que é exatamente o ponto do
   * símbolo. O `cubic-bezier` de saída faz a desaceleração parecer inércia, e
   * não corte.
   */
  useEffect(() => {
    if (!pronto || !visivel) return;

    const svg = svgRef.current;
    const bola = svg?.querySelector<SVGCircleElement>('.bola');
    if (!svg || !bola) {
      aoPousar?.();
      return;
    }

    // Quem pediu menos movimento não recebe animação nenhuma.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      svg.classList.add('pronto');
      aoPousar?.();
      return;
    }

    bola.style.offsetDistance = getComputedStyle(bola).offsetDistance;
    bola.style.animation = 'none';

    const quadro = requestAnimationFrame(() => {
      bola.style.transition = 'offset-distance .7s cubic-bezier(.16,1,.3,1)';
      bola.style.offsetDistance = '100%';
      svg.classList.add('pronto');
    });

    const fim = setTimeout(() => aoPousar?.(), 760);
    return () => {
      cancelAnimationFrame(quadro);
      clearTimeout(fim);
    };
  }, [pronto, visivel, aoPousar]);

  if (!visivel) return null;

  return (
    <div className="carregando" role="status" aria-live="polite">
      <svg
        ref={svgRef}
        className={`cerchia-spin${tamanho < 20 ? ' so-esfera' : ''}`}
        width={tamanho}
        height={Math.round(tamanho * 0.709)}
        viewBox="-80.34 -56.95 160.68 113.9"
        aria-hidden="true"
      >
        {/* rotate(-25) é a inclinação do símbolo; a órbita herda dela. */}
        <g transform="rotate(-25)">
          <ellipse className="trilho" cx="0" cy="0" rx="63.6" ry="37.9" />
          <circle className="bola" r="12.7" />
        </g>
      </svg>
      {texto && <span>{texto}</span>}
    </div>
  );
}
